import { FilterableTable } from 'components/tables/filterable-table';
import { PropertyFilterToken } from '@cloudscape-design/collection-hooks';
import { formatISO } from 'date-fns';
import {
  fromApiResponses,
  UpgradeAssessmentsTableElement,
} from 'pages/upgrade_assessments/data-model';
import { UpgradeAssessmentBadgeStatus } from 'pages/upgrade_assessments/helpers';
import { useSplitPanel } from 'contexts/split-panel';
import { UpgradeAssessmentDetails } from 'pages/upgrade_assessments/details';
import CopyText from 'components/copy-text';
import { UpgradeAssessmentStatus } from 'api/admin-models';
import { useListUpgradeAssessmentsInfinite } from 'api/admin-infinite';

export function AccountUpgradeAssessments(props: {
  orgId: string;
  accountId: string;
}): JSX.Element {
  const updateSplitPanel = useSplitPanel();
  const { data, mutate } = useListUpgradeAssessmentsInfinite(props.accountId);
  const isGamma = import.meta.env.VITE_API_ENDPOINT.includes('chkk.dog');

  return (
    <FilterableTable<UpgradeAssessmentsTableElement>
      selectionMode="single"
      data={fromApiResponses(data)}
      columns={[
        {
          id: 'Id',
          label: 'ID',
          cell: (item) => (
            <CopyText
              copyText={item.Id}
              copyButtonLabel="Click to copy"
              successText="Copied"
              errorText="Failed to copy"
            />
          ),

          defaultVisible: true,

          sortingField: 'Id',
          filterOperators: ['=', '!='],
        },
        {
          id: 'Name',
          label: 'Name',
          cell: (item) => item.Name || '-',

          defaultVisible: true,

          sortingField: 'Name',
          filterOperators: ['=', '!=', ':', '!:'],
        },
        {
          id: 'ClusterId',
          label: 'Cluster ID',
          cell: (item) => (
            <CopyText
              copyText={item.ClusterId}
              copyButtonLabel="Click to copy"
              successText="Copied"
              errorText="Failed to copy"
            />
          ),
          defaultVisible: true,

          sortingField: 'ClusterId',
          filterOperators: ['=', '!='],
        },
        {
          id: 'Environment',
          label: 'Environment',
          cell: (item) => item.Environment || '-',

          defaultVisible: !isGamma,

          sortingField: 'Environment',
          filterOperators: ['=', '!=', ':', '!:'],
        },
        {
          id: 'Status',
          label: 'Status',
          cell: (item) => (
            <UpgradeAssessmentBadgeStatus value={item.Status || 'pending'} />
          ),

          defaultVisible: true,

          sortingField: 'Status',
          filterOperators: ['=', '!='],
          filterOptions: [
            'pending',
            'in-progress',
            'ready',
            'failed',
            'outdated',
            'archived',
          ],
        },
        {
          id: 'Creation',
          label: 'Created',
          cell: (item) => formatISO(new Date(item.Created)),

          defaultVisible: true,

          sortingField: 'Created',
        },
      ]}
      defaultSorting={{ field: 'Creation', direction: 'descending' }}
      defaultFilterQuery={(searchParams) => {
        const tokens: PropertyFilterToken[] = [];
        tokens.push({
          propertyKey: 'Status',
          value: UpgradeAssessmentStatus.archived,
          operator: '!=',
        });
        return Promise.resolve({ tokens: tokens, operation: 'and' });
      }}
      selectionTrackBy={(item) => `${item.AccountId}#${item.Id}`}
      onSelectionChange={(event) => {
        updateSplitPanel(
          <UpgradeAssessmentDetails
            upgrade={{
              orgId: props.orgId,
              accountId: event.detail.selectedItems[0].AccountId,
              upgradeId: event.detail.selectedItems[0].Id,
            }}
            onChange={() => void mutate()}
          />,
        );
      }}
    />
  );
}
